import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TaskService } from '../task.service';
import {
  ListMyTasksRequest, ListMyTasksResponse, Pagination,
  Task, TaskCurrentSprint, TaskFilter,
  TaskMutateMask, TaskState } from '../protogen/planning/service_pb';

enum AddMode {
  CURRENT = 'current',
  BACKLOG = 'backlog',
}

/**
 * A component that can be used to list all backlog tasks. It lets users filter and also
 * create a new task.
 */
@Component({
  selector: 'app-add-task',
  templateUrl: './add-task.component.html',
  styleUrls: ['./add-task.component.scss']
})
export class AddTaskComponent implements AfterViewInit, OnInit {
  dataLoaded: boolean = false;
  addMode: AddMode = AddMode.BACKLOG;
  filteredTasks: Task.AsObject[] = [];
  taskTitle: string = '';

  private backlogTasks: Task.AsObject[] = [];

  @ViewChild('taskInput') taskInput?: ElementRef;

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private taskService: TaskService) {
    this.addMode = this.activatedRoute.snapshot.params['mode'];
    this.fetchTasks();
  }

  ngOnInit(): void {
    // This is necessary because Angular tries to reuse components when routing.
    this.activatedRoute.params.subscribe((newParams: any) => {
      this.addMode = newParams['mode'];
    });
  }

  ngAfterViewInit() {
    this.taskInput?.nativeElement.focus();
  }

  /** Adds a task to the current sprint and routes back to it. */
  async addTaskToCurrent(task: Task.AsObject) {
    task.isCurrentSprint = true;
    const mask: TaskMutateMask = new TaskMutateMask().setIsCurrentSprint(true);
    try {
      await this.taskService.editTask(task, mask.toObject());
      this.router.navigateByUrl('/current');
    } catch (err) {
      console.error(err);
    }
  }

  clearTitle() {
    this.taskTitle = '';
    this.titleChanged('');
  }

  async createTask() {
    const isPickingCurrent = (this.addMode === AddMode.CURRENT);
    const task: Task = new Task()
                           .setTitle(this.taskTitle)
                           .setTaskState(TaskState.INACTIVE)
                           .setIsCurrentSprint(isPickingCurrent);
    const mask: TaskMutateMask = new TaskMutateMask()
                                     .setTitle(true)
                                     .setTaskState(true)
                                     .setIsCurrentSprint(true);
    try {
      this.clearTitle();
      await this.taskService.createTask(task.toObject(), mask.toObject());
      if (isPickingCurrent) {
        this.router.navigateByUrl('/current');
      } else {
        this.fetchTasks();
      }
    } catch (err) {
      console.error(err);
    }
  }

  async deleteTask(task: Task.AsObject) {
    await this.taskService.deleteTask(task);
    await this.fetchTasks();
  }

  async moveToCurrent(task: Task.AsObject) {
    const mask: TaskMutateMask = new TaskMutateMask().setIsCurrentSprint(true);
    task.isCurrentSprint = true;
    const response = await this.taskService.editTask(task, mask.toObject());
    await this.fetchTasks();
  }

  titleChanged(newTitle: string) {
    this.filteredTasks = this.backlogTasks.filter(task => {
      return task.title.toLowerCase().includes(newTitle.toLowerCase());
    });
  }

  private async fetchOnePageOfTasks(continuationToken: string|null = null): Promise<ListMyTasksResponse.AsObject> {
    const req = new ListMyTasksRequest().setFilter(
        new TaskFilter()
            .setTaskCurrentSprint(TaskCurrentSprint.NOT_CURRENT_SPRINT)
            .addTaskState(TaskState.INACTIVE)
            .addTaskState(TaskState.IN_PROGRESS));
    if (continuationToken) {
      req.setPagination(new Pagination().setContinuationToken(continuationToken));
    }
    return this.taskService.listMyTasks(req);
  }

  private async fetchTasks() {
    let tasksList: Task.AsObject[] = [];
    let continuationToken = undefined;
    let doneFetching = false;
    while (!doneFetching) {
      const pageResponse: ListMyTasksResponse.AsObject =
          await this.fetchOnePageOfTasks(continuationToken);
      tasksList = tasksList.concat(pageResponse.tasksList);
      if (pageResponse.continuationToken) {
        continuationToken = pageResponse.continuationToken;
      } else {
        doneFetching = true;
        this.dataLoaded = true;
      }
    }

    this.dataLoaded = true;
    this.backlogTasks = tasksList;
    this.filteredTasks = tasksList.slice(0);
  }
}
